/* ===== Dues: the member's own view ===== */

import { useEffect, useMemo, useState } from 'react'
import type { TenantContext } from '@zetyar/data-contracts'
import {
  addPeriods,
  formatPeriodLabel,
  periodFromDate,
  periodRange,
  type MembershipDue,
  type OrgDuesSettings,
} from '@zetyar/domain'
import { Building2, Copy, CreditCard } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Modal } from '@/presentation/components/ui/Modal'
import { webDuesRepository } from '@/infrastructure/repositories/dues'
import { DueStatusBadge } from './DueStatusBadge'
import { PAYMENT_METHOD_LABELS, duesErrorMessage, formatMoney } from './duesFormat'

interface MyDuesViewProps {
  readonly context: TenantContext
  readonly membershipId: string
  readonly settings: OrgDuesSettings
  readonly organizationName: string
}

const HISTORY_LIMIT = 24
const QUICK_MONTHS = [1, 2, 3, 6, 12]

export function MyDuesView({ context, membershipId, settings, organizationName }: MyDuesViewProps) {
  const [dues, setDues] = useState<MembershipDue[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isBankOpen, setIsBankOpen] = useState(false)
  const [isPayOpen, setIsPayOpen] = useState(false)
  const [months, setMonths] = useState(1)
  const [isStarting, setIsStarting] = useState(false)
  const [payError, setPayError] = useState<string | null>(null)
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const currentPeriod = periodFromDate(new Date())

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setError(null)
    webDuesRepository
      .listMemberDues(context, membershipId, { limit: HISTORY_LIMIT })
      .then((rows) => {
        if (!cancelled) setDues(rows)
      })
      .catch((caught) => {
        console.error('Error al cargar tus cuotas:', caught)
        if (!cancelled) setError(duesErrorMessage(caught, 'No se pudieron cargar tus cuotas.'))
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [context, membershipId])

  const duesByPeriod = useMemo(() => new Map(dues.map((due) => [due.period, due])), [dues])
  const currentDue = duesByPeriod.get(currentPeriod) ?? null

  // An online payment starts at the first month from now that is not paid, exempt or in process.
  const firstOpenPeriod = useMemo(() => {
    let period = currentPeriod
    for (let guard = 0; guard < HISTORY_LIMIT && duesByPeriod.has(period); guard++) {
      period = addPeriods(period, 1)
    }
    return period
  }, [currentPeriod, duesByPeriod])

  const periodsToPay = useMemo(() => periodRange(firstOpenPeriod, months), [firstOpenPeriod, months])
  const periodsBlocked = periodsToPay.some((period) => duesByPeriod.has(period))

  const bankRows = [
    { label: 'Banco', value: settings.bankName, copyable: false },
    { label: 'Tipo de cuenta', value: settings.accountType, copyable: false },
    { label: 'Número de cuenta', value: settings.accountNumber, copyable: true },
    { label: 'Titular', value: settings.accountHolder, copyable: false },
    { label: 'RUT', value: settings.accountRut, copyable: true },
    { label: 'Correo para comprobante', value: settings.accountEmail, copyable: true },
  ].filter((row): row is { label: string; value: string; copyable: boolean } => Boolean(row.value))
  const hasBankDetails = bankRows.length > 0
  const canPayOnline = settings.onlinePaymentEnabled && Boolean(webDuesRepository.startOnlinePayment)

  const copyValue = (value: string, field: string) => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    })
  }

  const startPayment = async () => {
    const startOnlinePayment = webDuesRepository.startOnlinePayment
    if (!startOnlinePayment || periodsBlocked) return
    setIsStarting(true)
    setPayError(null)
    try {
      const { initPoint } = await startOnlinePayment(context, { startPeriod: firstOpenPeriod, months })
      window.location.href = initPoint
    } catch (caught) {
      console.error('Error al iniciar el pago en línea de cuotas:', caught)
      setPayError(duesErrorMessage(caught))
      setIsStarting(false)
    }
  }

  const statusText = (() => {
    if (currentDue?.status === 'paid') {
      return `Tu cuota de este mes está pagada${currentDue.paidAt ? ` desde el ${new Date(currentDue.paidAt).toLocaleDateString('es-CL')}` : ''}.`
    }
    if (currentDue?.status === 'exempt') return 'Estás exento de la cuota de este mes por decisión de la directiva.'
    if (currentDue?.status === 'processing') {
      return 'Tienes un pago en línea en proceso. Se confirmará automáticamente cuando Mercado Pago lo apruebe.'
    }
    return canPayOnline
      ? 'Tu cuota de este mes está pendiente. Puedes pagarla en línea o por transferencia.'
      : 'Tu cuota de este mes está pendiente. Si pagas por transferencia, la tesorería registrará tu pago al recibir el comprobante.'
  })()

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-[13px] bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-300">
                <CreditCard className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-base">Mi cuota de {formatPeriodLabel(currentPeriod)}</CardTitle>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  {formatMoney(settings.monthlyAmount, settings.currency)} mensuales · vence el día {settings.dueDay}
                </p>
              </div>
            </div>
            {!isLoading && !error && <DueStatusBadge status={currentDue?.status ?? null} />}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <p role="alert" className="text-sm text-danger-600 dark:text-danger-300">{error}</p>
          ) : (
            <p className="text-sm text-neutral-700 dark:text-neutral-300">{isLoading ? 'Cargando tu estado...' : statusText}</p>
          )}
          {(canPayOnline || hasBankDetails) && (
            <div className="flex flex-wrap gap-2">
              {canPayOnline && (
                <Button
                  leftIcon={<CreditCard className="h-4 w-4" />}
                  disabled={isLoading}
                  onClick={() => {
                    setMonths(1)
                    setPayError(null)
                    setIsPayOpen(true)
                  }}
                >
                  {currentDue ? 'Adelantar cuotas en línea' : 'Pagar en línea'}
                </Button>
              )}
              {hasBankDetails && (
                <Button variant="outline" leftIcon={<Building2 className="h-4 w-4" />} onClick={() => setIsBankOpen(true)}>
                  Datos para transferencia
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Mi historial de cuotas</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Cargando...</p>
          ) : dues.length === 0 ? (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">Aún no hay cuotas registradas a tu nombre.</p>
          ) : (
            <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {dues.map((due) => (
                <li key={due.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{formatPeriodLabel(due.period)}</p>
                    <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                      {[
                        due.paymentMethod ? PAYMENT_METHOD_LABELS[due.paymentMethod] : null,
                        due.paidAt ? `Pagada el ${new Date(due.paidAt).toLocaleDateString('es-CL')}` : null,
                        due.notes,
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'Sin detalle'}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    {due.status !== 'exempt' && (
                      <span className="text-sm font-bold text-neutral-800 dark:text-neutral-200">
                        {formatMoney(due.amount, due.currency)}
                      </span>
                    )}
                    <DueStatusBadge status={due.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Modal
        isOpen={isBankOpen}
        onClose={() => setIsBankOpen(false)}
        title="Datos para transferencia"
        footer={<Button variant="outline" onClick={() => setIsBankOpen(false)}>Cerrar</Button>}
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            Datos de pago informados por la tesorería de {organizationName}.
          </p>
          <dl className="space-y-3 rounded-[13px] border border-neutral-200 p-4 dark:border-neutral-800">
            {bankRows.map((row) => (
              <div key={row.label} className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <dt className="text-xs text-neutral-500 dark:text-neutral-400">{row.label}</dt>
                  <dd className="break-all text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.value}</dd>
                </div>
                {row.copyable && (
                  <Button size="sm" variant="ghost" aria-label={`Copiar ${row.label}`} onClick={() => copyValue(row.value, row.label)}>
                    {copiedField === row.label ? 'Copiado' : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                )}
              </div>
            ))}
          </dl>
          {settings.instructions && (
            <p className="rounded-[13px] bg-warning-50 px-3 py-2 text-xs text-warning-700 dark:bg-warning-500/15 dark:text-warning-100">
              {settings.instructions}
            </p>
          )}
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            La tesorería registrará tu pago cuando reciba la transferencia.
          </p>
        </div>
      </Modal>

      <Modal
        isOpen={isPayOpen}
        onClose={() => {
          if (!isStarting) setIsPayOpen(false)
        }}
        title="Pagar cuotas en línea"
        footer={
          <>
            <Button variant="outline" onClick={() => setIsPayOpen(false)} disabled={isStarting}>
              Cancelar
            </Button>
            <Button onClick={() => void startPayment()} isLoading={isStarting} disabled={periodsBlocked}>
              Ir a Mercado Pago
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <p className="mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-200">Cantidad de meses</p>
            <div className="grid grid-cols-5 gap-2">
              {QUICK_MONTHS.map((count) => (
                <button
                  key={count}
                  type="button"
                  aria-pressed={months === count}
                  onClick={() => setMonths(count)}
                  className={`rounded-[13px] border px-2 py-2 text-xs font-semibold ${
                    months === count
                      ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-200'
                      : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800'
                  }`}
                >
                  {count} {count === 1 ? 'mes' : 'meses'}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-[13px] bg-neutral-50 p-4 dark:bg-neutral-800/60">
            <p className="text-xs text-neutral-500 dark:text-neutral-400">Meses incluidos</p>
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              {periodsToPay.map((period) => formatPeriodLabel(period)).join(', ')}
            </p>
            <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">Total</p>
            <p className="text-xl font-bold text-primary-600 dark:text-primary-300">
              {formatMoney(settings.monthlyAmount * months, settings.currency)}
            </p>
          </div>
          {periodsBlocked && (
            <p className="text-sm text-warning-700 dark:text-warning-200">
              Alguno de esos meses ya está pagado, exento o en proceso. Elige menos meses.
            </p>
          )}
          {payError && <p role="alert" className="text-sm text-danger-600 dark:text-danger-300">{payError}</p>}
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Serás redirigido a Mercado Pago. El pago se confirma automáticamente cuando Mercado Pago lo aprueba; no necesitas
            enviar comprobante.
          </p>
        </div>
      </Modal>
    </>
  )
}
